import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';

import { useAccess } from '@/common/hooks/useAccess';
import { ControlFlagDocIdvRequiredAndCompletedControlValues } from '@getethos/ethos-global-lib';
import { RootState } from '../../app/rootReducer';
import { AppDispatch } from '../../app/store';
import {
  canDoUWxAction,
  canSeeReleaseTransferAuthorization,
  canViewHipaa,
  canViewPIIData,
  isAdmin,
} from '../../common/actionHelper';
import {
  automatedEvidencesUrl,
  getExistingPoliciesEndPoint,
  xhrFormat,
} from '../../common/api.endPoints';
import {
  CaseLockProvider,
  useCaseLockAccess,
  useCaseLockActionGuard,
} from '../../common/caseLocking/CaseLockContext';
import { ExternalLink } from '../../common/components/ExternalLink';
import { Loading } from '../../common/components/Loading';
import { FlexGrid, Spacer } from '../../common/components/v2';
import { AdminDocumentType } from '../../common/constants/adminDocumentType';
import { Citizenship } from '../../common/constants/citizenship';
import { DecisionType } from '../../common/constants/decision';
import { DocumentType } from '../../common/constants/documentType';
import { Resource } from '../../common/constants/resource';
import {
  isUnderwriterCopilot,
  LegacyCarrierRoles,
  NoraPermissionV2,
  Roles,
} from '../../common/constants/roles';
import { TaskState } from '../../common/constants/states';
import { UserOrganization } from '../../common/constants/userOrganization';
import ElementLocators from '../../common/ElementLocators';
import { FeatureFlagsContext } from '../../common/featureFlagsWrapper';
import { HttpService } from '../../common/http.service';
import { IKeyValMap } from '../../common/interfaces/IKeyValMap';
import { ProductFactory } from '../../common/productConfig/productFactory';
import useAnalytics from '../../useAnalytics';
import { SnackBarType } from '../layout/interfaces/ISnacksProps';
import { LeftSidebar } from '../layout/sidebars/LeftSidebar';
import { RightSidebarV2 } from '../layout/sidebars/RightSidebarV2';
import { SnackbarContext } from '../layout/SnackbarContext';
import { AuthContext } from '../login/AuthWrapper';
import { EvidenceInfo } from '../PPWB/dtos/evidenceInfo.dto';
import { fetchEvidenceList } from '../PPWB/helpers';
import { ITask, ITaskWithNotes } from '../tasks/interfaces/ITask';
import {
  DECISION_TASK_DEFAULT_ID,
  getAllTasks,
  NOTES_TASK_DEFAULT_ID,
  OVERVIEW_TASK_DEFAULT_ID,
} from '../tasks/slice/taskListSlice';
import {
  carrierEligibilityRetry,
  deleteAttachment,
  deleteDisputeAttachment,
  deleteEvidence,
  evidenceInfoRetry,
  fetchTasks,
  fetchWaiveReasons,
  markTaskOutdated,
  resetApplication,
  sendEmail,
  updateTaskState,
  uploadAttachment,
  uploadEvidence,
  uploadLetter,
  upsertETI,
} from '../tasks/slice/taskThunks';
import { taskUiActions } from '../tasks/slice/taskUiSlice';
import { TaskTypes } from '../tasks/types/taskTypes';
import { AdminDocuments } from './adminDocuments/AdminDocuments';
import { AutomatedEvidence } from './autoEvidence/AutomatedEvidence';
import { fetchAutomatedEvidences } from './autoEvidence/automatedEvidenceSlice';
import { CaseLockBanner } from './caseLock/CaseLockBanner';
import { CASE_LOCK_BANNER_HEIGHT_PX } from './caseLock/constants';
import { Decision } from './decision/Decision';
import { fetchDecisions } from './decision/decisionSlice';
import { EditDecisionLeadingToCLRManager } from './decision/EditDecisionLeadingToCLRManager';
import { fetchApplication } from './detailSlice';
import DoctorInfo from './doctorInfo/DoctorInfo';
import { ExistingPolicies } from './ExistingPolicies';
import { FamilyCoverageSection } from './FamilyCoverageSection';
import { DetailsHeader } from './header/DetailsHeader';
import { InfoBanner } from './header/InfoBanner';
import { WarningBanner } from './header/WarningBanner';
import { IdentificationDocLink } from './IdentificationDocument';
import { IBundledPolicyDetails } from './interfaces/IBundledPolicyDetails';
import { IDecision } from './interfaces/IDecision';
import { LinkedPolicies } from './LinkedPolicies';
import { Notes } from './notes/Notes';
import { NotesBottomDrawer } from './notes/NotesBottomDrawer';
import { Overview } from './Overview';
import { Reassign } from './reassign/Reassign';
import { CaseReferralSection } from './referral/CaseReferralSection';
import { RuleResults } from './ruleResults/RuleResults';
import { RuwReasons } from './ruwReasons/RuwReasons';
import styles from './styles.module.scss';
import Summary from './summary/Summary';
import { APSDetail } from './tasks/APSDetail';
import { AutoMedicalClaimsDetail } from './tasks/AutoMedicalClaimsDetail';
import { CarrierEligibilityDetail } from './tasks/CarrierEligibility';
import { CriminalRecordsDetail } from './tasks/CriminalRecordsDetail';
import { DisclosureDetail } from './tasks/disclosure/DisclosureDetail';
import { DisputeTaskDetail } from './tasks/DisputeTaskDetail';
import { EHRDetail } from './tasks/EHRDetail';
import { ETIDetail } from './tasks/ETIDetail';
import { ETIFormData } from './tasks/etiGenerator/types';
import { InsuranceLabsDetail } from './tasks/InsuranceLabsDetail';
import { LabPiQDetail } from './tasks/LabPiQDetail';
import { LetterDrawerProvider } from './tasks/letter/drawer';
import { LetterDetail } from './tasks/letter/LetterDetail';
import { MedExamDetail } from './tasks/MedExamDetail';
import { MIBDetail } from './tasks/mib/MIBDetail';
import { MIBCodebackDetail } from './tasks/MIBCodebackDetail';
import { MIBCodeDetailsDetail } from './tasks/MIBCodeDetailsDetail';
import { MVRDetail } from './tasks/mvr/MVRDetail';
import { OrderMIBCodebackDetail } from './tasks/OrderMIBCodebackDetail';
import { PlanFTaskDetail } from './tasks/PlanFTaskDetail';
import { QEInterviewQnA } from './tasks/QEInterviewQnA';
import { ReinstatementDetail } from './tasks/ReinstatementDetail';
import { RuleReviewDetail } from './tasks/RuleReview';
import { RxDetail } from './tasks/rx/RxDetail';
import { SimpleUploadTaskDetail } from './tasks/SimpleUploadTaskDetail';
import { SimpleUploadTaskWithNotesDetail } from './tasks/SimpleUploadTaskWithNotesDetail';
import { TaskEmptyDetail } from './tasks/TaskEmptyDetail';
import { TaskErrorBoundary } from './tasks/TaskErrorBoundary';
import { TRLDetail } from './tasks/trl/TRLDetail';
import { getCaseDetailNavTaskSegments } from './utilities/evidenceTaskOrder';
import { isTaskViewable } from './utilities/taskDetailUtilities';

// import { IDecision } from './interfaces/IDecision';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

type SectionRefsType = Record<string, number | null>;

export const Detail = () => {
  const { id: applicationId } = useParams<{ id: string }>();
  if (applicationId === undefined) {
    return <Navigate to={{ pathname: '/' }} />;
  }

  const dispatch = useDispatch<AppDispatch>();
  const [applicationFetchStatus, setApplicationFetchStatus] = useState('idle');
  const [taskFetchStatus, setTaskFetchStatus] = useState('idle');
  const [decisionFetchStatus, setDecisionFetchStatus] = useState('idle');
  const [isUwPaused, setIsUwPaused] = useState(false);
  const [editingDecision, setEditingDecision] = useState<IDecision | null>(
    null,
  );
  const [existingApplications, setExistingApplications] = useState(null);
  const [automatedEvidenceFetchStatus, setAutomatedEvidenceFetchStatus] =
    useState('idle');
  const [waiveReasonsFetchStatus, setWaiveReasonsFetchStatus] =
    useState('idle');
  const [externalLink, setExternalLink] = useState<string | undefined>(
    undefined,
  );
  const [selectedDocumentTitle, setSelectedDocumentTitle] = useState<
    string | undefined
  >(undefined);
  const [evidenceInfo, setEvidenceInfo] = useState<EvidenceInfo[] | null>(null);
  const [familyCoverageHasContent, setFamilyCoverageHasContent] =
    useState(false);
  const { openSnacks, setSnacks, openSnack } = useContext(SnackbarContext);
  const auth = useContext(AuthContext);
  const { flags } = useContext(FeatureFlagsContext);
  const caseLock = useCaseLockAccess();
  const assertCaseActionAllowed = useCaseLockActionGuard(caseLock);
  const applicationReady =
    applicationFetchStatus === 'success' &&
    waiveReasonsFetchStatus === 'success' &&
    decisionFetchStatus === 'success' &&
    taskFetchStatus === 'success' &&
    evidenceInfo !== null &&
    automatedEvidenceFetchStatus === 'success';

  const isError = [
    applicationFetchStatus,
    taskFetchStatus,
    decisionFetchStatus,
    automatedEvidenceFetchStatus,
  ].includes('error');

  const updateSnacks = (updatedSnacks: any) => {
    setSnacks(updatedSnacks);
  };

  const policyNumber = useSelector(
    state => state.application.carrierPolicyNumber,
  );
  const isAmlDocAvailable = useSelector(
    state => state.application.isAmlDocAvailable,
  );
  const product = useSelector(state => state.application.product);
  const carrier = useSelector(state => state.application.carrier);
  const subCategory = useSelector(state => state.application.subCategory);
  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory,
  );
  const termPolicyId = useSelector(state => state.application.termPolicyId);
  const isActivated = useSelector(state => state.application.activated);
  const isAudit = useSelector(state => state.application.isAudit);
  const decisions = useSelector(state => state.decision.decisions);
  const applicationLifecycle = useSelector(
    state => state.application.applicationLifecycleV2,
  );
  const decisionTask = useSelector(state =>
    getAllTasks(state.taskList).find(t => t.taskType === TaskTypes.DECISION),
  );
  const notesTask = useSelector(state =>
    getAllTasks(state.taskList).find(t => t.taskType === TaskTypes.NOTES),
  );
  const bundledPolicyDetails: IBundledPolicyDetails | null = useSelector(
    (state: RootState) => state.application.bundledPolicyDetails,
  );
  const stackedCarrierPolicyNumbers =
    bundledPolicyDetails && bundledPolicyDetails.isStacked
      ? [bundledPolicyDetails.carrierPolicyNumber]
      : [];
  const bundledCarrierPolicyNumber =
    bundledPolicyDetails && !bundledPolicyDetails.isStacked
      ? bundledPolicyDetails.carrierPolicyNumber
      : undefined;
  const bundledPolicyId =
    bundledPolicyDetails && !bundledPolicyDetails.isStacked
      ? bundledPolicyDetails.policyId
      : undefined;
  const { logEvent } = useAnalytics();
  const sectionRefs = useRef<SectionRefsType>({});
  const isSummaryEnabled = false;
  const citizenship = useSelector(state => state.application.citizenship);
  const citizenshipDocumentType = useSelector(
    state => state.application.citizenshipDocumentType,
  );
  const nonResidentUwControlMethod = useSelector(
    state => state.application.nonResidentUwControlMethod,
  );
  const isReconsiderationProfile = useSelector(
    state => state.application.isReconsiderationProfile,
  );

  const riders = useMemo(
    () =>
      decisions.find(
        ({ decisionType, deleteReason }) =>
          DecisionType.FINAL === decisionType && !deleteReason,
      )?.ineligibleRiders ?? [],
    [decisions],
  );

  const canSeeAutomatedEvidence = useAccess({
    permission: NoraPermissionV2.VIEW_DOWNLOAD_RAW_EVIDENCE,
    legacyCheck: isAdmin,
  });

  const canViewPii = useAccess({
    permission: NoraPermissionV2.VIEW_PII_DATA,
    legacyCheck: canViewPIIData,
  });

  const canViewHipaaForm = useAccess({
    permission: NoraPermissionV2.VIEW_HIPAA_AUTH_FORM,
    legacyCheck: canViewHipaa,
  });

  // Do not use isAdmin for legacyCheck — it includes WORKBENCH_ADMIN which is a v2 role.
  const canViewPolicyCombinedDoc = useAccess({
    permission: NoraPermissionV2.VIEW_POLICY_COMBINED_DOC,
    legacyCheck: roles =>
      roles.includes(Roles.ADMIN) ||
      LegacyCarrierRoles.some(r => roles.includes(r)),
  });

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = document.querySelectorAll('[data-section-id]');
      sectionElements.forEach(element => {
        const isVisible =
          element.getBoundingClientRect().top < window.innerHeight - 150 &&
          element.getBoundingClientRect().bottom > 150;
        const sectionName = element.getAttribute('data-section-id');
        handleVisibilityChange(sectionName!, isVisible);
      });
    };

    const handleVisibilityChange = (
      sectionName: string,
      isVisible: boolean,
    ) => {
      if (!isVisible && sectionRefs.current[sectionName]) {
        const sectionStartTime = sectionRefs.current[sectionName];
        const currentTime = new Date().getTime();
        const timeSpent = currentTime - sectionStartTime!;

        if (timeSpent > 15000) {
          logEvent('Time spent on the section', {
            sectionName,
            timeSpent,
          });
        }

        sectionRefs.current[sectionName] = null;
      } else if (isVisible && !sectionRefs.current[sectionName]) {
        sectionRefs.current[sectionName] = new Date().getTime();
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    // Update the document title using the browser API
    if (policyNumber) {
      document.title = `${policyNumber}${Resource.NORA_M_DASH}${Resource.APPLICATION_TITLE}`;
      logEvent('Application Details Page Opened', {
        policyNumber,
      });
    }
  }, [policyNumber]);

  useEffect(() => {
    async function fetchEvidenceListApi() {
      try {
        const evidenceList = await fetchEvidenceList(
          product,
          carrier,
          termPolicyId.toString(),
          true,
        );
        setEvidenceInfo(evidenceList ?? []);
      } catch (error) {
        setEvidenceInfo([]);
        console.error(error);
      }
    }
    if (applicationFetchStatus === 'success' && evidenceInfo === null) {
      fetchEvidenceListApi();
    }
  }, [applicationFetchStatus]);

  useEffect(() => {
    (async () => {
      const uwPauseStatus =
        await HttpService.getInstance().get(`health/pauseStatus`);
      setIsUwPaused(uwPauseStatus.parsedBody);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      dispatch(resetApplication());
      dispatch(
        fetchApplication(
          applicationId,
          setApplicationFetchStatus,
          openSnacks,
          updateSnacks,
        ),
      );
      dispatch(
        fetchTasks(applicationId, setTaskFetchStatus, openSnacks, updateSnacks),
      );
      dispatch(
        fetchDecisions(
          applicationId,
          setDecisionFetchStatus,
          openSnacks,
          updateSnacks,
        ),
      );
      dispatch(
        fetchWaiveReasons(setWaiveReasonsFetchStatus, openSnacks, updateSnacks),
      );
      dispatch(
        fetchAutomatedEvidences(
          applicationId,
          setAutomatedEvidenceFetchStatus,
          openSnacks,
          updateSnacks,
        ),
      );
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const response = await HttpService.getInstance().get(
        getExistingPoliciesEndPoint(applicationId),
      );
      if (response && !response.error) {
        // TODO: support subCategory after policy admin and maestro support
        setExistingApplications(response.parsedBody);
      }
    })();
  }, []);

  const handleBeforeUnload = () => {
    const currentTime = new Date().getTime();
    Object.entries(sectionRefs.current).forEach(
      ([sectionName, sectionStartTime]) => {
        if (sectionStartTime) {
          const timeSpent = currentTime - sectionStartTime;
          if (timeSpent > 15000) {
            logEvent('Time spent on the section', {
              sectionName,
              timeSpent,
              flush: true,
            });
          }
        }
      },
    );
    sectionRefs.current = {};
    logEvent('Application Details Page Closed', {
      flush: true,
    });
  };

  const uploadEvidenceCb = async (
    existingTaskData: ITask,
    files: any,
    type: string,
  ) => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    const fileSize: number[] = [];
    const formData = new FormData();
    for (const file of files) {
      fileSize.push((file as File).size);
      formData.append('files', file);
    }
    formData.append('documentType', type);
    formData.append('documentSubType', '');
    formData.append('documentSize', JSON.stringify(fileSize));
    dispatch(
      uploadEvidence(
        applicationId,
        existingTaskData,
        formData,
        openSnacks,
        updateSnacks,
      ),
    );
  };

  const deleteDisputeAttachmentCb = async (
    existingTaskData: ITask,
    documentId: number,
    _reason: string,
  ) => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    dispatch(
      deleteDisputeAttachment(
        existingTaskData,
        documentId,
        openSnacks,
        updateSnacks,
      ),
    );
  };

  const deleteEvidenceCb = async (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    dispatch(
      deleteEvidence(
        applicationId,
        existingTaskData,
        documentId,
        reason,
        openSnacks,
        updateSnacks,
      ),
    );
  };

  const openFileAtUri = async (uri: string, fileName: string) => {
    const response: IKeyValMap = await HttpService.getInstance().get(
      xhrFormat(uri),
    );
    if (
      (response && response.error && response.parsedBody) ||
      !response?.parsedBody?.url
    ) {
      openSnack({
        id: uuidv4(),
        type: SnackBarType.ALERT,
        message: `Unable to download ${fileName}. Please try later.`,
      });
    } else {
      /// open file in new tab
      if (
        flags.drawer_external_link &&
        isUnderwriterCopilot(auth.roles) &&
        notesTask &&
        notesTask.id
      ) {
        setSelectedDocumentTitle(fileName);
        setExternalLink(response.parsedBody.url);
        dispatch(
          taskUiActions.updateActiveTask({
            activeTaskId: notesTask.id.toString(),
          }),
        );
        const el = document.getElementById(notesTask.id.toString()!);
        if (el) {
          window.scrollTo({
            top: el.offsetTop,
            behavior: 'smooth',
          });
        }
      } else {
        window.open(response.parsedBody.url, '_blank');
      }
    }
  };
  const uploadLetterHandler = async (
    taskId: number,
    files: any,
    type: string,
  ) => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    const formData = new FormData();
    formData.append('file', files[0]);
    formData.append('documentType', type);
    formData.append('documentSize', (files[0] as File).size.toString());
    dispatch(
      uploadLetter(applicationId, taskId, formData, openSnacks, updateSnacks),
    );
  };

  const uploadDisputeAttachmentHandler = async (
    existingTaskData: ITask,
    files: any,
    type: string,
  ) => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    const formData = new FormData();
    formData.append('file', files[0]);
    formData.append('documentType', type);
    formData.append('documentSize', (files[0] as File).size.toString());
    dispatch(
      uploadAttachment(
        applicationId,
        existingTaskData.id,
        formData,
        openSnacks,
        updateSnacks,
      ),
    );
  };

  const deleteAttachmentHandler = async (
    taskId: number,
    taskType: TaskTypes,
    attachmentId: number,
  ) => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    dispatch(deleteAttachment(taskId, taskType, attachmentId));
  };

  const sendEmailHandler = async (taskId: number, reason?: string) => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    dispatch(
      sendEmail(
        applicationId,
        taskId,
        termPolicyId,
        openSnacks,
        updateSnacks,
        reason,
      ),
    );
  };

  const retryEvidenceRequest = async (taskId: number) => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    dispatch(
      evidenceInfoRetry(applicationId, taskId, openSnacks, updateSnacks),
    );
  };

  const retryCarrierEligibilityData = async (taskId: number) => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    dispatch(
      carrierEligibilityRetry(applicationId, taskId, openSnacks, updateSnacks),
    );
  };

  const markEvidenceOutdated = async (taskId: number) => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    dispatch(markTaskOutdated(applicationId, taskId, openSnacks, updateSnacks));
  };

  const taskStateHandler = async (
    taskId: number,
    state: TaskState,
    reason?: string,
  ) => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    if (state) {
      dispatch(
        updateTaskState(
          applicationId,
          taskId,
          state,
          openSnacks,
          updateSnacks,
          reason,
        ),
      );
    }
  };

  const taskfeedbackHandler = async (
    taskId: number,
    sufficent: boolean,
    feedback: string,
  ) => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    if (taskId) {
      dispatch(
        updateTaskState(
          applicationId,
          taskId,
          TaskState.COMPLETED,
          openSnacks,
          updateSnacks,
          undefined,
          sufficent,
          feedback,
        ),
      );
    }
  };

  const upsertETIHandler = useCallback(
    async (formData: ETIFormData): Promise<boolean> => {
      if (!assertCaseActionAllowed()) {
        return false;
      }
      try {
        return await dispatch(
          upsertETI({
            formData,
            applicationId,
            openSnacks,
            updateSnacks,
          }),
        ).unwrap();
      } catch (_) {
        return false;
      }
    },
    [
      dispatch,
      applicationId,
      openSnacks,
      updateSnacks,
      assertCaseActionAllowed,
    ],
  );

  const { trlAndDisclosureTasks, rxAndMibTasks, otherEvidenceTasks } =
    useSelector(state => getCaseDetailNavTaskSegments(state.taskList));

  const isNotesDrawerOpen = useSelector(
    (state: RootState) => state.notesDraft.isOpen,
  );

  const getAutomatedEvidenceUrl = async (key: string, fileName: string) => {
    const response = await HttpService.getInstance().get(
      automatedEvidencesUrl(key),
    );
    if ((response && response.error) || !response?.parsedBody?.url) {
      openSnack({
        id: uuidv4(),
        type: SnackBarType.ALERT,
        message: `Unable to view ${fileName}. Please try later.`,
      });
    } else {
      window.open(response.parsedBody.url, '_blank');
    }
  };

  const shouldShowHipaaFormURL = (user: any, policyId: number): boolean => {
    if (!user || !policyId) {
      return false;
    }
    const validUserOrganization = new Set<UserOrganization>([
      UserOrganization.ETHOS,
      UserOrganization.ESP,
      UserOrganization.INTEGRA,
    ]);
    if (
      !validUserOrganization.has(user?.userOrganization as UserOrganization)
    ) {
      return false;
    }
    return canViewHipaaForm;
  };

  const shouldShowReleaseTransferAuthorization = (
    user: any,
    policyId: number,
  ): boolean => {
    if (!user || !policyId) {
      return false;
    }

    // Admin
    // UWx team
    // Ethos + Chief UW
    // Ethos + Chief CM
    // Integra + Case Manager
    // Ethos + Underwriter
    // Ethos + Production Underwriter
    // Ethos + Flex Underwriter
    return (
      canDoUWxAction(user.roles) ||
      ((user?.userOrganization as UserOrganization) ===
        UserOrganization.ETHOS &&
        canSeeReleaseTransferAuthorization(user.roles)) ||
      ((user?.userOrganization as UserOrganization) ===
        UserOrganization.INTEGRA &&
        user.roles?.some((r: string) => Roles.CASEMANAGER === r))
    );
  };

  const renderEvidenceTaskRows = (tasks: ITask[]) => {
    if (!tasks.length) {
      return null;
    }
    return tasks
      .map((t: ITask) => {
        if (!isTaskViewable(auth.userOrganization, t.taskType)) {
          return <></>;
        }
        return t.taskType === TaskTypes.EHR ? (
          <EHRDetail
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
            feedbackHandler={taskfeedbackHandler}
            applicationId={Number(applicationId)}
          />
        ) : t.taskType === TaskTypes.ETI ? (
          <ETIDetail
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
            feedbackHandler={taskfeedbackHandler}
            upsertHandler={upsertETIHandler}
            policyNumber={policyNumber}
          />
        ) : t.taskType === TaskTypes.RULE_REVIEW ? (
          <RuleReviewDetail
            key={t.id}
            task={t}
            taskStateHandler={taskStateHandler}
          />
        ) : t.taskType === TaskTypes.APS ? (
          <APSDetail
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
            feedbackHandler={taskfeedbackHandler}
          />
        ) : t.taskType === TaskTypes.MEDEXAM ? (
          <MedExamDetail
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
          />
        ) : t.taskType === TaskTypes.TRL ? (
          <TRLDetail
            key={t.id}
            task={t}
            retryHandler={retryEvidenceRequest}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
            markOutdatedHandler={markEvidenceOutdated}
          />
        ) : t.taskType === TaskTypes.MVR ? (
          <MVRDetail
            key={t.id}
            task={t}
            retryHandler={retryEvidenceRequest}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
            markOutdatedHandler={markEvidenceOutdated}
          />
        ) : t.taskType === TaskTypes.MIB ? (
          <MIBDetail
            key={t.id}
            task={t}
            retryHandler={retryEvidenceRequest}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
            markOutdatedHandler={markEvidenceOutdated}
          />
        ) : t.taskType === TaskTypes.RX ? (
          <RxDetail
            key={t.id}
            task={t}
            retryHandler={retryEvidenceRequest}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
            markOutdatedHandler={markEvidenceOutdated}
          />
        ) : t.taskType === TaskTypes.DISCLOSURE ? (
          <DisclosureDetail
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
            policyId={termPolicyId}
          />
        ) : t.taskType === TaskTypes.OTAF_LETTER ||
          t.taskType === TaskTypes.SUBSTANDARD_LETTER ||
          t.taskType === TaskTypes.DECLINE_LETTER ||
          t.taskType === TaskTypes.RUW_WITHDRAWL_LETTER ||
          t.taskType === TaskTypes.REINSTATEMENT_APPROVAL_LETTER ||
          t.taskType === TaskTypes.REINSTATEMENT_DECLINE_LETTER ||
          t.taskType === TaskTypes.REINSTATEMENT_WITHDRAWN_LETTER ||
          t.taskType === TaskTypes.SPECIAL_AUTHORIZATION ? (
          <LetterDetail
            key={t.id}
            task={t}
            uploadHandler={uploadLetterHandler}
            downloadHandler={openFileAtUri}
            taskStateHandler={taskStateHandler}
            deleteAttachmentHandler={deleteAttachmentHandler}
            sendEmailHandler={sendEmailHandler}
            riders={riders}
          />
        ) : t.taskType === TaskTypes.PLAN_F ? (
          <PlanFTaskDetail
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
          />
        ) : t.taskType === TaskTypes.REINSTATEMENT_APPLICATION ? (
          <ReinstatementDetail
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
          />
        ) : t.taskType === TaskTypes.MIB_CODEBACK ? (
          <MIBCodebackDetail
            key={t.id}
            task={t}
            taskStateHandler={taskStateHandler}
          />
        ) : t.taskType === TaskTypes.ORDER_MIB_CODEBACK ? (
          <OrderMIBCodebackDetail
            key={t.id}
            task={t}
            taskStateHandler={taskStateHandler}
          />
        ) : t.taskType === TaskTypes.LAB_PIQTURE ? (
          <LabPiQDetail
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
            applicationId={Number(applicationId)}
          />
        ) : t.taskType === TaskTypes.MEDICAL_CLAIMS ? (
          <SimpleUploadTaskDetail
            taskTitle={Resource.TASK_TITLE_MEDICAL_CLAIMS}
            elementLocator={ElementLocators.LOCATOR_MEDICAL_CLAIMS_SECTION}
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            documentType={DocumentType.MEDICAL_CLAIMS}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
          />
        ) : t.taskType === TaskTypes.AUTO_MEDICAL_CLAIMS ? (
          <AutoMedicalClaimsDetail
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
            retryHandler={retryEvidenceRequest}
            markOutdatedHandler={markEvidenceOutdated}
            showRunButton={
              productConfig?.hasAutoMedicalClaimRunButton() ?? false
            }
          />
        ) : t.taskType === TaskTypes.SCRIPT_CHECK ? (
          <SimpleUploadTaskDetail
            taskTitle={Resource.TASK_TITLE_SCRIPT_CHECK}
            elementLocator={ElementLocators.LOCATOR_SCRIPT_CHECK_SECTION}
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            documentType={DocumentType.SCRIPT_CHECK}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
          />
        ) : t.taskType === TaskTypes.MIB_CODE_DETAILS ? (
          <MIBCodeDetailsDetail
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
          />
        ) : t.taskType === TaskTypes.INSURANCE_LABS ? (
          <InsuranceLabsDetail
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
            feedbackHandler={taskfeedbackHandler}
          />
        ) : t.taskType === TaskTypes.CRIMINAL_RECORDS ? (
          <CriminalRecordsDetail
            key={t.id}
            task={t}
            uploadHandler={uploadEvidenceCb}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
            product={product}
            carrier={carrier}
            subCategory={subCategory}
            retryHandler={retryEvidenceRequest}
            markOutdatedHandler={markEvidenceOutdated}
          />
        ) : t.taskType === TaskTypes.ADDITIONAL_INFORMATION_FROM_CUSTOMER ? (
          <SimpleUploadTaskWithNotesDetail
            taskTitle={Resource.TASK_TITLE_ADDITIONAL_CUSTOMER_INFO}
            elementLocator={ElementLocators.LOCATOR_ADDITIONAL_INFORMATION_TASK}
            key={t.id}
            task={t as ITaskWithNotes}
            uploadHandler={uploadEvidenceCb}
            documentType={DocumentType.ADDITIONAL_INFORMATION_FROM_CUSTOMER}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
          />
        ) : t.taskType === TaskTypes.ADDITIONAL_DOCUMENTS ? (
          <SimpleUploadTaskWithNotesDetail
            taskTitle={Resource.TASK_TITLE_ADDITIONAL_DOCUMENTS}
            elementLocator={ElementLocators.LOCATOR_ADDITIONAL_DOCUMENTS_TASK}
            key={t.id}
            task={t as ITaskWithNotes}
            uploadHandler={uploadEvidenceCb}
            documentType={DocumentType.ADDITIONAL_DOCUMENTS}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteEvidenceCb}
            taskStateHandler={taskStateHandler}
          />
        ) : t.taskType === TaskTypes.DISPUTE ? (
          <DisputeTaskDetail
            key={t.id}
            task={t}
            uploadHandler={uploadDisputeAttachmentHandler}
            downloadHandler={openFileAtUri}
            deleteHandler={deleteDisputeAttachmentCb}
            taskStateHandler={taskStateHandler}
          />
        ) : t.taskType === TaskTypes.CARRIER_ELIGIBILITY ? (
          <CarrierEligibilityDetail
            key={t.id}
            task={t as ITaskWithNotes}
            taskStateHandler={taskStateHandler}
            retryHandler={retryCarrierEligibilityData}
          />
        ) : t.taskType === TaskTypes.APS_SUMMARY ? (
          <TaskEmptyDetail
            task={t}
            taskStateHandler={taskStateHandler}
            resourceName={Resource.TASK_TITLE_APS_SUMMARY}
            locator={ElementLocators.LOCATOR_APS_SUMMARY_SECTION}
          />
        ) : t.taskType === TaskTypes.EHR_SUMMARY ? (
          <TaskEmptyDetail
            task={t}
            taskStateHandler={taskStateHandler}
            resourceName={Resource.TASK_TITLE_EHR_SUMMARY}
            locator={ElementLocators.LOCATOR_EHR_SUMMARY_SECTION}
          />
        ) : (
          <React.Fragment key={t.id}></React.Fragment>
        );
      })
      .map((task, taskIdx) => (
        <div data-section-id={tasks[taskIdx].taskType}>
          <TaskErrorBoundary
            taskType={tasks[taskIdx].taskType}
            key={tasks[taskIdx].id}
            taskId={tasks[taskIdx].id}
          >
            {task}
          </TaskErrorBoundary>
        </div>
      ));
  };

  if (isError) {
    return <h5>{Resource.ERROR_MESSAGE}</h5>;
  }
  if (!applicationReady) {
    return (
      <>
        <Loading fullscreen={true} />
      </>
    );
  }
  return (
    <>
      {externalLink && (
        <ExternalLink
          externalLink={externalLink}
          setExternalLink={setExternalLink}
          title={selectedDocumentTitle}
        />
      )}
      <CaseLockProvider value={caseLock}>
        <LetterDrawerProvider
          enabled={
            !!(
              flags.isLetterAutomationEnabled &&
              flags.uwLetterBuilderDrawerEnabled
            )
          }
        >
          <div
            className={`${styles.Wrap} ${styles.WithNotesDrawer} ${
              caseLock.bannerMessage ? styles.WithCaseLockBanner : ''
            } ${isNotesDrawerOpen ? styles.WithNotesDrawerOpen : ''}`}
            style={
              caseLock.bannerMessage
                ? ({
                    '--case-lock-banner-height': `${CASE_LOCK_BANNER_HEIGHT_PX}px`,
                  } as React.CSSProperties)
                : undefined
            }
          >
            <LeftSidebar familyCoverageHasContent={familyCoverageHasContent} />
            <div className={styles.Main}>
              <div className={styles.Centered}>
                {/* Establishes our main Flexbox Grid for Center content */}
                <FlexGrid>
                  <div className={styles.detailsContainer}>
                    <div>
                      {isUwPaused && <WarningBanner />}
                      <DetailsHeader />
                      {<Reassign applicationId={applicationId} />}
                    </div>
                    {isSummaryEnabled &&
                      isUnderwriterCopilot(auth.roles) &&
                      evidenceInfo && <Summary evidences={evidenceInfo} />}
                    <Decision
                      title={Resource.TASK_TITLE_DECISION}
                      product={product}
                      carrier={carrier}
                      subCategory={subCategory}
                      decisions={decisions}
                      decisionTask={decisionTask}
                      key={DECISION_TASK_DEFAULT_ID}
                      onEditDecision={
                        caseLock.isLockedForActions
                          ? undefined
                          : setEditingDecision
                      }
                      applicationLifecycle={applicationLifecycle}
                    />
                    <RuwReasons applicationId={applicationId} />
                    <EditDecisionLeadingToCLRManager
                      decisionToEdit={editingDecision}
                      onClose={() => setEditingDecision(null)}
                    />
                    {isReconsiderationProfile && (
                      <InfoBanner message="Applicant is under reconsideration" />
                    )}
                    <RuleResults
                      applicationId={applicationId}
                      decisions={decisions}
                      termPolicyId={termPolicyId}
                    />
                    <Overview key={OVERVIEW_TASK_DEFAULT_ID} />
                    {productConfig?.supportsFamilyCoverageDetails() &&
                      applicationId && (
                        <FamilyCoverageSection
                          applicationId={parseInt(applicationId, 10)}
                          onHasContent={setFamilyCoverageHasContent}
                        />
                      )}
                    {flags.isNewLinkedPoliciesSectionEnabled ? (
                      <LinkedPolicies
                        policyId={termPolicyId}
                        policyNumber={policyNumber}
                        policies={existingApplications}
                        fileOpenHandler={openFileAtUri}
                        stackedCarrierPolicyNumbers={
                          stackedCarrierPolicyNumbers
                        }
                        stackedTermPolicyIds={undefined}
                        bundledCarrierPolicyNumber={bundledCarrierPolicyNumber}
                        bundledPolicyId={bundledPolicyId}
                      />
                    ) : (
                      <ExistingPolicies
                        policyId={termPolicyId}
                        policyNumber={policyNumber}
                        policies={existingApplications}
                        stackedCarrierPolicyNumbers={
                          stackedCarrierPolicyNumbers
                        }
                        stackedTermPolicyIds={undefined}
                        bundledCarrierPolicyNumber={bundledCarrierPolicyNumber}
                        bundledPolicyId={bundledPolicyId}
                      />
                    )}
                    {renderEvidenceTaskRows(trlAndDisclosureTasks)}
                    <DoctorInfo
                      applicationId={applicationId}
                      product={product}
                      carrier={carrier}
                      policyId={termPolicyId}
                    />
                    {renderEvidenceTaskRows(rxAndMibTasks)}
                    {isAudit && (
                      <QEInterviewQnA
                        policyId={termPolicyId}
                        carrierPolicyNumber={policyNumber}
                      />
                    )}
                    {renderEvidenceTaskRows(otherEvidenceTasks)}
                    <Notes
                      applicationId={applicationId}
                      key={NOTES_TASK_DEFAULT_ID}
                    />
                    {flags.isCaseReferralEnabled &&
                      auth.userOrganization === UserOrganization.ETHOS && (
                        <CaseReferralSection applicationId={applicationId} />
                      )}
                    {canSeeAutomatedEvidence && (
                      <AutomatedEvidence
                        getAutomatedEvidenceUrl={getAutomatedEvidenceUrl}
                      />
                    )}
                    {isActivated &&
                      termPolicyId &&
                      productConfig?.showPolicyCombined(
                        auth.roles,
                        auth.userOrganization,
                      ) &&
                      canViewPolicyCombinedDoc && (
                        <AdminDocuments
                          policyId={+termPolicyId}
                          product={product}
                          carrier={carrier}
                          documentType={AdminDocumentType.POLICY_COMBINED}
                          downloadHandler={openFileAtUri}
                        />
                      )}
                    {shouldShowHipaaFormURL(auth, termPolicyId) && (
                      <AdminDocuments
                        policyId={+termPolicyId}
                        product={product}
                        carrier={carrier}
                        documentType={AdminDocumentType.HIPAA_COMPLETED}
                        downloadHandler={openFileAtUri}
                      />
                    )}
                    {flags.specialAuthEnabled &&
                      shouldShowReleaseTransferAuthorization(
                        auth,
                        termPolicyId,
                      ) && (
                        <AdminDocuments
                          policyId={+termPolicyId}
                          product={product}
                          carrier={carrier}
                          documentType={
                            AdminDocumentType.RELEASE_TRANSFER_AUTHORIZATION
                          }
                          downloadHandler={openFileAtUri}
                          hideIfNotAvailable={true}
                        />
                      )}
                    {citizenship &&
                      [Citizenship.NON_VISA_HOLDER, Citizenship.VISA_HOLDER]
                        .map(it => it.toString())
                        .includes(citizenship) &&
                      nonResidentUwControlMethod ===
                        'document_idv_verified_auto' && (
                        <IdentificationDocLink
                          applicationId={Number(applicationId)}
                          citizenshipDocumentType={citizenshipDocumentType}
                          policyId={termPolicyId}
                          controlValue={
                            ControlFlagDocIdvRequiredAndCompletedControlValues.uw_control
                          }
                        />
                      )}

                    {isAmlDocAvailable && canViewPii && (
                      <IdentificationDocLink
                        applicationId={Number(applicationId)}
                        citizenshipDocumentType={citizenshipDocumentType}
                        policyId={termPolicyId}
                        controlValue={
                          ControlFlagDocIdvRequiredAndCompletedControlValues.aml_control
                        }
                      />
                    )}
                  </div>
                </FlexGrid>
                <Spacer height={24} />
              </div>
            </div>
            <RightSidebarV2 />
            {caseLock.bannerMessage && (
              <CaseLockBanner message={caseLock.bannerMessage} />
            )}
          </div>
          <NotesBottomDrawer applicationId={applicationId} />
        </LetterDrawerProvider>
      </CaseLockProvider>
    </>
  );
};
